// API Cache Utility for performance optimization
class ApiCache {
  constructor() {
    this.cache = new Map();
    this.defaultCacheDuration = 5 * 60 * 1000; // 5 minutes
  }

  // Generate cache key from URL and options
  generateCacheKey(url, options = {}) {
    const { method = 'GET', body } = options;
    const bodyStr = body ? JSON.stringify(body) : '';
    return `${method}:${url}:${bodyStr}`;
  }

  // Check if cached data is still valid
  isValid(cacheEntry, customDuration) {
    if (!cacheEntry) return false;
    const duration = customDuration || this.defaultCacheDuration;
    return Date.now() - cacheEntry.timestamp < duration;
  }

  // Get cached data if valid
  get(url, options = {}, customDuration) {
    const key = this.generateCacheKey(url, options);
    const cached = this.cache.get(key);
    
    if (this.isValid(cached, customDuration)) {
      console.log(`Cache HIT for ${url}`);
      return cached.data;
    }
    
    console.log(`Cache MISS for ${url}`);
    return null;
  }

  // Set data in cache
  set(url, data, options = {}) {
    const key = this.generateCacheKey(url, options);
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  // Clear specific cache entry
  clear(url, options = {}) {
    const key = this.generateCacheKey(url, options);
    this.cache.delete(key);
  }

  // Clear all cache
  clearAll() {
    this.cache.clear();
  }

  // Clear expired entries
  cleanup() {
    for (const [key, entry] of this.cache.entries()) {
      if (!this.isValid(entry)) {
        this.cache.delete(key);
      }
    }
  }

  // Get cache statistics
  getStats() {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys())
    };
  }
}

// Create singleton instance
const apiCache = new ApiCache();

// Enhanced fetch function with caching and timeout
export const fetchWithCache = async (url, options = {}, cacheDuration) => {
  // For non-GET requests, don't use cache
  if (options.method && options.method !== 'GET') {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - API took too long to respond');
      }
      throw error;
    }
  }

  // Try to get from cache first
  const cachedData = apiCache.get(url, options, cacheDuration);
  if (cachedData) {
    console.log(`🚀 Cache HIT for ${url} - instant response!`);
    return cachedData;
  }

  try {
    // Add timeout to requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout for first request
    
    console.log(`⏳ Cache MISS for ${url} - fetching from API...`);
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Cache the successful response
    apiCache.set(url, data, options);
    console.log(`✅ Successfully cached response for ${url}`);
    
    return data;
  } catch (error) {
    console.error(`❌ Fetch error for ${url}:`, error);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - API is taking too long to respond. Please try again or contact support.');
    }
    throw error;
  }
};

// Performance monitoring utility
export const performanceMonitor = {
  measureApiCall: async (name, promise) => {
    const start = performance.now();
    try {
      const result = await promise;
      const duration = performance.now() - start;
      console.log(`🚀 API Call ${name}: ${duration.toFixed(2)}ms`);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      console.log(`❌ API Call ${name} failed after ${duration.toFixed(2)}ms:`, error);
      throw error;
    }
  },

  measureRender: (componentName) => {
    const start = performance.now();
    return () => {
      const duration = performance.now() - start;
      if (duration > 16) { // Log if render takes longer than 16ms (60fps threshold)
        console.log(`⚠️ Slow Render ${componentName}: ${duration.toFixed(2)}ms`);
      }
    };
  }
};

// Debounce utility for search inputs
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Fetch all pages of paginated data
export const fetchAllPages = async (baseUrl, options = {}) => {
  console.log(`📖 Starting paginated fetch for ${baseUrl}`);
  const allData = [];
  let page = 1;
  let hasMore = true;
  
  while (hasMore) {
    try {
      const url = `${baseUrl}?page=${page}&limit=50`;
      console.log(`⏳ Fetching page ${page} from ${url}`);
      
      const data = await performanceMonitor.measureApiCall(
        `${baseUrl}-page-${page}`,
        fetchWithCache(url, options)
      );
      
      if (Array.isArray(data) && data.length > 0) {
        allData.push(...data);
        console.log(`✅ Page ${page}: ${data.length} records (Total: ${allData.length})`);
        
        // If we got less than 50 records, we've reached the end
        hasMore = data.length === 50;
        page++;
      } else {
        hasMore = false;
      }
    } catch (error) {
      console.error(`❌ Error fetching page ${page}:`, error);
      // Don't break on error, return what we have
      hasMore = false;
    }
  }
  
  console.log(`🎉 Completed paginated fetch: ${allData.length} total records`);
  return allData;
};

// Fetch multiple endpoints in parallel
export const fetchMultipleEndpoints = async (endpoints) => {
  console.log(`🚀 Starting parallel fetch for ${endpoints.length} endpoints`);
  
  const promises = endpoints.map(({ name, url, options }) => 
    performanceMonitor.measureApiCall(
      name,
      fetchAllPages(url, options)
    ).catch(error => {
      console.error(`❌ Error fetching ${name}:`, error);
      return []; // Return empty array on error
    })
  );
  
  const results = await Promise.all(promises);
  console.log(`✅ Parallel fetch complete`);
  
  return results;
};

// Export cache instance for manual control
export { apiCache };

// Auto cleanup every 10 minutes
setInterval(() => {
  apiCache.cleanup();
}, 10 * 60 * 1000);
